import React from 'react';
import Grid from '@material-ui/core/Grid';
import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';
import { makeStyles, Theme } from '@material-ui/core/styles';
import Popover from 'components/@common/Popover';
import RadialChart from 'components/@common/Charts/RadialChart';
import Skeleton from 'components/@common/Skeleton';
import { generateSeriesPercentRound } from 'utilities/radial-chart-util';
import { ChartCardType } from '../types';

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    height: '100%',
    boxSizing: 'border-box',
    paddingTop: theme.spacing(1),
    paddingRight: theme.spacing(3),
    paddingBottom: theme.spacing(1),
    paddingLeft: theme.spacing(3)
  },
  chartContainer: {
    height: '100%'
  },
  chart: {
    maxWidth: 225,
    margin: '0 auto'
  },
  textContainer: {
    alignSelf: 'center',
    minWidth: 200
  },
  text: {},
  skeleton: { margin: 'auto' }
}));

export interface ChartData {
  progress?: number;
  total?: number;
}

export interface RadialChartCardProps extends ChartData {
  title: string;
  helpText?: string;
  color: string;
  type: ChartCardType;
  loading: boolean;
}

const RadialChartCard: React.FC<RadialChartCardProps> = ({
  progress = 0,
  total = 0,
  title,
  helpText,
  color,
  type,
  loading
}): React.ReactElement => {
  const classes = useStyles();
  const titleComponent = (
    <Typography variant="body2" component="h3">
      {title}
    </Typography>
  );

  return (
    <Paper className={classes.container}>
      {loading ? (
        <Skeleton width={190} height={190} className={classes.skeleton} />
      ) : (
        <Grid container className={classes.chartContainer}>
          <Grid item className={classes.chart}>
            <RadialChart
              height={190}
              series={[generateSeriesPercentRound(progress, total)]}
              options={{
                chart: {
                  sparkline: { enabled: false }
                },
                labels: [],
                colors: [color],
                grid: {
                  padding: { top: -30, right: 0, bottom: -30, left: -10 }
                },
                plotOptions: {
                  radialBar: {
                    offsetX: 10,
                    hollow: { size: '32%' },
                    dataLabels: {
                      name: { show: false },
                      total: { show: false },
                      value: {
                        show: true,
                        offsetY: 6,
                        fontSize: '28px',
                        fontWeight: 500
                      }
                    }
                  }
                }
              }}
            />
          </Grid>
          <Grid item className={classes.textContainer}>
            {helpText ? <Popover description={helpText}>{titleComponent}</Popover> : titleComponent}
            {type === ChartCardType.PERCENT ? (
              <Typography variant="h2" component="div">
                {loading ? <Skeleton /> : `${Math.round((progress / total) * 100)}%`}
              </Typography>
            ) : (
              <div>
                {loading ? (
                  <Typography variant="h2">
                    <Skeleton width={250} />
                  </Typography>
                ) : (
                  <>
                    <Typography variant="h2" component="span" className={classes.text}>
                      {progress}
                    </Typography>
                    <Typography component="span"> of {total}</Typography>
                  </>
                )}
              </div>
            )}
          </Grid>
        </Grid>
      )}
    </Paper>
  );
};

export default RadialChartCard;
